// React hook that wires native push into the authenticated app.
//
// Mounted once inside the tenant Layout (rendered under AuthGuard, so an agent
// is authenticated and we're inside the Router — useNavigate works). It is a
// NO-OP in a browser: initPush() returns immediately when not native.
//
// On mount (native + authenticated): request permission, register with FCM, and
// set up the tap→deep-link handler using the router's navigate(). On unmount it
// tears the listeners down. Logout-time unregistration lives in AuthContext
// (it must run while the session cookie is still valid).

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { initPush, isNativePlatform, type PushDeepLink } from './native';
import { INBOX_PATH } from '../lib/routeConfig';

export function usePushRegistration(isAuthenticated: boolean): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativePlatform() || !isAuthenticated) return;

    let cleanup: (() => void) | null = null;
    let cancelled = false;

    const onDeepLink = (target: PushDeepLink) => {
      if (target.kind === 'ticket') {
        navigate(`/tickets/${target.ticketId}`);
      } else {
        navigate(`${INBOX_PATH}?conversation=${encodeURIComponent(target.phone)}`);
      }
    };

    void initPush(onDeepLink).then((teardown) => {
      if (cancelled) teardown();
      else cleanup = teardown;
    });

    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [isAuthenticated, navigate]);
}
